import { useCallback, useEffect, useState } from "react";
import { getPublicWorkerResume, submitEmployerResumeDecision } from "./publicResumeApi.js";
import "./publicWorkerResume.css";

/**
 * The page an EMPLOYER opens from the link an operator sent them, and the two
 * buttons that are the whole point of it.
 *
 * NO ACCOUNT, NO SESSION, NO NAVIGATION. The employer received one URL about
 * one worker; there is nothing else here to reach, and deliberately no menu,
 * no search and no link to the rest of the platform. The token in the URL is
 * the only thing that identifies them, so this page must not behave as though
 * it knows who they are beyond that.
 *
 * THE DECISION IS FINAL AND THE PAGE SAYS SO BEFORE IT IS MADE. The backend is
 * first-answer-wins, and a link that has been answered can only ever report the
 * answer afterwards. An employer who taps รับเข้าทำงาน by accident has no undo,
 * so the confirm step is not a nicety - it is the only protection there is.
 *
 * WHAT AN EMPLOYER MAY SEE is decided on the server (PublicWorkerResumeService)
 * and is a curated subset: no passport number, no citizen number, no internal
 * ids, no Agent. This page renders only what arrives, and renders a missing
 * value as a visible gap rather than an empty line that reads like a zero.
 */

const DECISIONS = { Accepted: "Accepted", Rejected: "Rejected" };

const CONFIRM_COPY = {
  [DECISIONS.Accepted]: {
    title: "ยืนยันรับเข้าทำงาน",
    body: "เมื่อกดยืนยันแล้วจะแก้ไขไม่ได้ และเจ้าหน้าที่จะเริ่มขั้นตอนเตรียมตัวให้แรงงานเข้าทำงาน",
    confirm: "ยืนยันรับเข้าทำงาน",
    tone: "accept",
  },
  [DECISIONS.Rejected]: {
    title: "ยืนยันไม่รับ",
    body: "เมื่อกดยืนยันแล้วจะแก้ไขไม่ได้ แรงงานรายนี้จะกลับไปรอเสนอกับงานอื่น",
    confirm: "ยืนยันไม่รับ",
    tone: "reject",
  },
};

function readTokenFromUrl() {
  if (typeof window === "undefined") {
    return null;
  }
  const token = new URLSearchParams(window.location.search).get("token");
  return token && token.trim().length > 0 ? token.trim() : null;
}

export default function PublicWorkerResumePage() {
  const [token] = useState(readTokenFromUrl);
  const [state, setState] = useState({ status: "loading", resume: null, message: null });
  const [pending, setPending] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const load = useCallback(() => {
    if (!token) {
      setState({ status: "invalid", resume: null, message: "ลิงก์นี้ไม่ถูกต้อง กรุณาตรวจสอบลิงก์ที่ได้รับอีกครั้ง" });
      return;
    }
    setState({ status: "loading", resume: null, message: null });
    getPublicWorkerResume(token)
      .then((resume) => setState({ status: "ready", resume, message: null }))
      .catch((error) =>
        setState({
          status: "invalid",
          resume: null,
          // The server's own sentence - it is the only text that tells the
          // employer whether to ask for a new link or stop. PublicApiError
          // carries it on .message (publicRequest lifts body.message into the
          // Error itself), not on a .body the way apiClient does.
          message: error?.message ?? "เปิดลิงก์นี้ไม่สำเร็จ กรุณาติดต่อเจ้าหน้าที่ที่ส่งลิงก์ให้ท่าน",
        }),
      );
  }, [token]);

  useEffect(load, [load]);

  async function submitDecision(decision) {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const resume = await submitEmployerResumeDecision({ token, decision, note });
      setState({ status: "ready", resume, message: null });
      setPending(null);
      setNote("");
    } catch (error) {
      setSubmitError(error?.message ?? "บันทึกคำตอบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      // A 409 means somebody already answered on this link. Re-reading turns a
      // stale page into the truth instead of leaving a dead button on screen.
      if (error?.status === 409) {
        load();
        setPending(null);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (state.status === "loading") {
    return (
      <main className="wx-pwr">
        <p className="wx-pwr-state">กำลังเปิดประวัติ...</p>
      </main>
    );
  }

  if (state.status === "invalid") {
    return (
      <main className="wx-pwr">
        <section className="wx-pwr-card wx-pwr-card--state" role="alert">
          <h1>เปิดลิงก์นี้ไม่ได้</h1>
          <p>{state.message}</p>
        </section>
      </main>
    );
  }

  const resume = state.resume;
  const decided = resume?.decision?.decision ?? null;

  return (
    <main className="wx-pwr">
      <header className="wx-pwr-head">
        <p className="wx-pwr-eyebrow">Workmate X</p>
        <h1>ประวัติแรงงานที่เสนอให้ท่านพิจารณา</h1>
        {resume?.job?.jobTitle && (
          <p className="wx-pwr-job">
            ตำแหน่ง <strong>{resume.job.jobTitle}</strong>
            {resume.job.employerName ? ` · ${resume.job.employerName}` : ""}
          </p>
        )}
      </header>

      <section className="wx-pwr-card">
        <div className="wx-pwr-identity">
          {resume?.photoUrl ? (
            <img className="wx-pwr-photo" src={resume.photoUrl} alt="" />
          ) : (
            // Never a stranger's face and never a silent blank: an employer
            // deciding on a person should know a photo is simply not on file.
            <div className="wx-pwr-photo wx-pwr-photo--empty" aria-hidden="true">
              ไม่มีรูป
            </div>
          )}
          <div className="wx-pwr-identity-text">
            <h2>{resume?.fullName ?? "ไม่ระบุชื่อ"}</h2>
            <p className="wx-pwr-code">{resume?.workerCode ?? "-"}</p>
            <ul className="wx-pwr-quickfacts">
              <Fact label="สัญชาติ" value={resume?.nationality} />
              <Fact label="อายุ" value={resume?.age ? `${resume.age} ปี` : null} suffixless />
              <Fact
                label="ประสบการณ์"
                value={resume?.yearsOfExperience != null ? `${resume.yearsOfExperience} ปี` : null}
                suffixless
              />
            </ul>
          </div>
        </div>

        <dl className="wx-pwr-details">
          <Detail label="จังหวัดที่อยากทำงาน" value={resume?.preferredProvinces} />
          <Detail label="เริ่มงานได้ตั้งแต่" value={formatDate(resume?.availableFromDate)} />
          <Detail
            label="ค่าจ้างที่คาดหวัง"
            value={resume?.expectedSalaryAmount ? `${Number(resume.expectedSalaryAmount).toLocaleString("th-TH")} บาท` : null}
          />
        </dl>

        <Block title="ทักษะ" empty="ยังไม่มีข้อมูลทักษะในระบบ" show={resume?.skills?.length > 0}>
          <ul className="wx-pwr-chips">
            {resume?.skills?.map((skill) => (
              <li key={skill}>{skill}</li>
            ))}
          </ul>
        </Block>

        <Block title="ภาษา" empty="ยังไม่มีข้อมูลภาษาในระบบ" show={resume?.languages?.length > 0}>
          <ul className="wx-pwr-list">
            {resume?.languages?.map((language) => (
              <li key={language.language}>
                <strong>{language.language}</strong>
                {language.level ? <span> · {language.level}</span> : null}
              </li>
            ))}
          </ul>
        </Block>

        <Block title="ประสบการณ์ทำงาน" empty="ยังไม่มีประวัติการทำงานในระบบ" show={resume?.workHistory?.length > 0}>
          <ul className="wx-pwr-list">
            {resume?.workHistory?.map((entry, index) => (
              <li key={`${entry.employerName}-${index}`}>
                <strong>{entry.position ?? "ไม่ระบุตำแหน่ง"}</strong>
                {entry.employerName ? <span> · {entry.employerName}</span> : null}
                {entry.country ? <span> · {entry.country}</span> : null}
                {entry.durationText ? <span className="wx-pwr-muted"> ({entry.durationText})</span> : null}
              </li>
            ))}
          </ul>
        </Block>
      </section>

      {decided ? (
        <section className={`wx-pwr-card wx-pwr-answered wx-pwr-answered--${decided.toLowerCase()}`} role="status">
          <h2>{decided === DECISIONS.Accepted ? "ท่านรับแรงงานรายนี้เข้าทำงานแล้ว" : "ท่านตอบว่าไม่รับแรงงานรายนี้"}</h2>
          <p>
            {decided === DECISIONS.Accepted
              ? "เจ้าหน้าที่จะติดต่อกลับเพื่อดำเนินการขั้นตอนถัดไป"
              : "ขอบคุณสำหรับคำตอบ เจ้าหน้าที่จะเสนอแรงงานรายอื่นให้ท่านพิจารณา"}
          </p>
          {resume?.decision?.note && <p className="wx-pwr-muted">หมายเหตุที่ท่านระบุไว้: {resume.decision.note}</p>}
        </section>
      ) : pending ? (
        <section className={`wx-pwr-card wx-pwr-confirm wx-pwr-confirm--${CONFIRM_COPY[pending].tone}`}>
          <h2>{CONFIRM_COPY[pending].title}</h2>
          <p>{CONFIRM_COPY[pending].body}</p>
          <label className="wx-pwr-note-label" htmlFor="wx-pwr-note">
            หมายเหตุถึงเจ้าหน้าที่ (ไม่บังคับ)
          </label>
          <textarea
            id="wx-pwr-note"
            className="wx-pwr-note"
            rows={3}
            value={note}
            maxLength={1000}
            onChange={(event) => setNote(event.target.value)}
          />
          {submitError && (
            <p className="wx-pwr-error" role="alert">
              {submitError}
            </p>
          )}
          <div className="wx-pwr-actions">
            <button
              type="button"
              className="wx-pwr-button wx-pwr-button--ghost"
              onClick={() => setPending(null)}
              disabled={submitting}
            >
              ย้อนกลับ
            </button>
            <button
              type="button"
              className={`wx-pwr-button wx-pwr-button--${CONFIRM_COPY[pending].tone}`}
              onClick={() => submitDecision(pending)}
              disabled={submitting}
            >
              {submitting ? "กำลังบันทึก..." : CONFIRM_COPY[pending].confirm}
            </button>
          </div>
        </section>
      ) : (
        <section className="wx-pwr-card wx-pwr-decide">
          <h2>ท่านต้องการรับแรงงานรายนี้เข้าทำงานหรือไม่</h2>
          <p className="wx-pwr-muted">เมื่อตอบแล้วจะแก้ไขไม่ได้</p>
          <div className="wx-pwr-actions">
            <button
              type="button"
              className="wx-pwr-button wx-pwr-button--reject"
              onClick={() => setPending(DECISIONS.Rejected)}
            >
              ไม่รับ
            </button>
            <button
              type="button"
              className="wx-pwr-button wx-pwr-button--accept"
              onClick={() => setPending(DECISIONS.Accepted)}
            >
              รับเข้าทำงาน
            </button>
          </div>
        </section>
      )}
    </main>
  );
}

/**
 * The API sends a plain calendar date ("yyyy-MM-dd"). Parsed with explicit
 * parts rather than new Date(string), which would read it as UTC midnight and
 * can show the previous day to a reader east of Greenwich - which is everyone
 * this page is for.
 */
function formatDate(value) {
  if (!value) {
    return null;
  }
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) {
    return value;
  }
  return new Date(year, month - 1, day).toLocaleDateString("th-TH", { dateStyle: "long" });
}

function Fact({ label, value }) {
  return (
    <li>
      <span>{label}</span>
      <strong>{value ?? "ไม่ระบุ"}</strong>
    </li>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value ?? "ไม่ระบุ"}</dd>
    </div>
  );
}

/** A section that states its own absence rather than vanishing silently. */
function Block({ title, empty, show, children }) {
  return (
    <div className="wx-pwr-block">
      <h3>{title}</h3>
      {show ? children : <p className="wx-pwr-muted">{empty}</p>}
    </div>
  );
}
